import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GoogleFlightsBookingLinkResponse } from '@dreamai/types';
import api from '../api';
import './booking-page.scss';

const BookingPage: React.FC = () => {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const [bookingData, setBookingData] =
    useState<GoogleFlightsBookingLinkResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);

  useEffect(() => {
    const fetchBookingData = async () => {
      if (!key) {
        setError('No booking key provided');
        setLoading(false);
        return;
      }

      try {
        // Make request without authentication since route is now public
        const response = await api.get<GoogleFlightsBookingLinkResponse>(
          `/google-flights-booking-links/${key}`,
        );

        if (response.data.bookingUrl && response.data.postData) {
          setBookingData(response.data);
          // Start 5-second countdown after data is loaded
          setCountdown(5);
        } else {
          setError('Invalid booking data received');
        }
      } catch (error) {
        console.error('Error fetching booking data:', error);
        setError(
          'Failed to load booking information. The link may be expired or invalid.',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchBookingData();
  }, [key]);

  // Auto-redirect countdown effect
  useEffect(() => {
    if (countdown === null || countdown <= 0) return;

    const timer = setTimeout(() => {
      if (countdown === 1) {
        // Time's up, redirect
        handleBookingSubmit();
      } else {
        // Decrement countdown
        setCountdown(countdown - 1);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, bookingData]);

  const handleBookingSubmit = () => {
    if (!bookingData) return;

    // Create a form that submits the URL-encoded POST data (same as SpecialMessageArtifacts)
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = bookingData.bookingUrl;
    form.target = '_self'; // Don't open new window, navigate in same tab
    form.style.display = 'none';
    form.enctype = 'application/x-www-form-urlencoded';

    // Parse the URL-encoded POST data and create form fields
    const params = new URLSearchParams(bookingData.postData);
    params.forEach((value, key) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  if (loading) {
    return (
      <div className='booking-page'>
        <div className='booking-container'>
          <div className='loading-spinner'>
            <div className='spinner'></div>
            <p>Loading your booking information...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='booking-page'>
        <div className='booking-container'>
          <div className='error-message'>
            <h2>Booking Link Error</h2>
            <p>{error}</p>
            <button className='back-button' onClick={() => navigate('/')}>
              Go to Homepage
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!bookingData) {
    return (
      <div className='booking-page'>
        <div className='booking-container'>
          <div className='error-message'>
            <h2>No Booking Data</h2>
            <p>Unable to load booking information.</p>
            <button className='back-button' onClick={() => navigate('/')}>
              Go to Homepage
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='booking-page'>
      <div className='booking-container'>
        <div className='booking-header'>
          <h1>Complete Your Flight Booking</h1>
          <p>You're about to be redirected to complete your flight booking.</p>
        </div>

        <div className='booking-details'>
          <div className='provider-info'>
            <h3>Booking Provider</h3>
            <p>{bookingData.bookWith || 'Flight Booking Provider'}</p>
          </div>

          {countdown !== null && countdown > 0 && (
            <div className='countdown-info'>
              <p>Automatically redirecting in {countdown} seconds...</p>
            </div>
          )}

          <div className='booking-actions'>
            <button className='book-button' onClick={handleBookingSubmit}>
              Continue to {bookingData.bookWith || 'Booking Site'}
            </button>
          </div>
        </div>

        <div className='booking-footer'>
          <p className='disclaimer'>
            You will be redirected to the booking provider's website to complete
            your purchase. This link is secure and will expire after 24 hours.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
